import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { v4 as uuidv4 } from 'uuid';
import { hasTrackingConsent } from '@/components/rgpd/CookieConsentBanner';

const SESSION_KEY = 'nauticoach_session_id';
const SESSION_START_KEY = 'nauticoach_session_start';
const PAGE_COUNT_KEY = 'nauticoach_page_count';
const LAST_VISIT_ID_KEY = 'nauticoach_last_visit_id';

const getSessionId = (): string => {
  let sessionId = sessionStorage.getItem(SESSION_KEY);
  if (!sessionId) {
    sessionId = uuidv4();
    sessionStorage.setItem(SESSION_KEY, sessionId);
    sessionStorage.setItem(SESSION_START_KEY, Date.now().toString());
    sessionStorage.setItem(PAGE_COUNT_KEY, '0');
  }
  return sessionId;
};

const getSessionStartTime = (): number => {
  const start = sessionStorage.getItem(SESSION_START_KEY);
  return start ? parseInt(start, 10) : Date.now();
};

const incrementPageCount = (): number => {
  const count = parseInt(sessionStorage.getItem(PAGE_COUNT_KEY) || '0', 10) + 1;
  sessionStorage.setItem(PAGE_COUNT_KEY, count.toString());
  return count;
};

const getDeviceType = (): string => {
  const ua = navigator.userAgent;
  if (/tablet|ipad|playbook|silk/i.test(ua)) {
    return 'tablet';
  }
  if (/mobile|iphone|ipod|android|blackberry|opera mini|iemobile/i.test(ua)) {
    return 'mobile';
  }
  return 'desktop';
};

interface GeoData {
  country?: string;
  city?: string;
  countryCode?: string;
}

const fetchGeoData = async (): Promise<GeoData> => {
  try {
    // ip-api.com ne supporte pas HTTPS en gratuit, on utilise ipapi.co à la place
    const response = await fetch('https://ipapi.co/json/');
    if (response.ok) {
      const data = await response.json();
      return {
        country: data.country_name,
        city: data.city,
        countryCode: data.country_code,
      };
    }
  } catch (error) {
    console.log('Geolocation not available');
  }
  return {};
};

export const useVisitorTracking = () => {
  const location = useLocation();
  const trackedPagesRef = useRef<Set<string>>(new Set());
  const lastVisitIdRef = useRef<string | null>(sessionStorage.getItem(LAST_VISIT_ID_KEY));

  // Mettre à jour la durée de session quand l'utilisateur quitte
  const updateSessionDuration = useCallback(async () => {
    const visitId = lastVisitIdRef.current;
    if (!visitId) return;

    const sessionStart = getSessionStartTime();
    const durationSeconds = Math.floor((Date.now() - sessionStart) / 1000);
    const pageCount = parseInt(sessionStorage.getItem(PAGE_COUNT_KEY) || '1', 10);
    const isBounce = pageCount <= 1;

    try {
      await supabase
        .from('visites')
        .update({ 
          duree_session: durationSeconds,
          is_bounce: isBounce
        })
        .eq('id', visitId);
    } catch (error) {
      console.log('Session update failed');
    }
  }, []);

  // Écouter la fermeture de page pour mettre à jour la durée
  useEffect(() => {
    const handleBeforeUnload = () => {
      updateSessionDuration();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        updateSessionDuration();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [updateSessionDuration]);

  useEffect(() => {
    const trackVisit = async () => {
      // Ne pas tracker si l'utilisateur n'a pas consenti aux cookies analytiques
      if (!hasTrackingConsent()) {
        return;
      }

      const pageKey = `${location.pathname}`;
      
      // Ne tracker qu'une fois par page par session
      if (trackedPagesRef.current.has(pageKey)) {
        return;
      }
      
      trackedPagesRef.current.add(pageKey);
      const pageCount = incrementPageCount();

      try {
        const sessionId = getSessionId();
        const geoData = await fetchGeoData();
        const deviceType = getDeviceType();
        
        // Get current user if logged in
        const { data: { user } } = await supabase.auth.getUser();

        const { data, error } = await supabase.from('visites').insert({
          session_id: sessionId,
          user_id: user?.id || null,
          page_url: location.pathname,
          pays: geoData.country || null,
          ville: geoData.city || null,
          code_pays: geoData.countryCode || null,
          user_agent: navigator.userAgent,
          referrer: document.referrer || null,
          device_type: deviceType,
          is_bounce: pageCount === 1, // Sera mis à jour à la fin de la session
        }).select('id').single();

        if (data?.id) {
          lastVisitIdRef.current = data.id;
          sessionStorage.setItem(LAST_VISIT_ID_KEY, data.id);
        }
      } catch (error) {
        console.log('Visit tracking failed:', error);
      }
    };

    trackVisit();
  }, [location.pathname]);
};
